import { useEffect, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import PageWrapper from 'components/Common/PageWraper';
import CustomizedTable from '../../CustomizedTable/CustomizedTable';
import { useDataReconciliation } from './hooks/useDataReconciliation';
import { MASTER_RECON_CHECKS, TRANSACTIONAL_RECON_CHECKS } from './constants';

const getStyledColumns = (columns: any[]) => columns.map((column) => ({
  ...column,
  cellClass: column.key.includes('_vs_')
    ? `${column.cellClass || ''} reconciliation-status-cell`
    : column.cellClass,
  formatter: ({ row }: any) => {
    const formattedValue = column.formatter({ row });
    const normalizedValue = String(formattedValue).trim().toUpperCase();

    if (column.key.includes('_vs_') && normalizedValue === 'N/A') {
      return (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#A9ADCB33 !important',
            color: '#71758F !important',
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          Not Run
        </Box>
      );
    }

    if (normalizedValue === 'N/A') {
      return <Typography sx={{ color: '#A9ADCB', fontWeight: 500, fontSize: '14px' }}>N/A</Typography>;
    }

    if (column.key.includes('_vs_') && (normalizedValue === 'MATCH' || normalizedValue === 'MISMATCH')) {
      const reconCheck = [...MASTER_RECON_CHECKS, ...TRANSACTIONAL_RECON_CHECKS]
        .find((check) => check.statusKey === column.key);
      const formatCount = (value: unknown) => {
        const text = String(value ?? '').trim();
        if (!text || text.toUpperCase() === 'N/A') return null;
        const numericValue = Number(text.replace(/,/g, ''));
        return Number.isFinite(numericValue) ? numericValue.toLocaleString('en-US') : text;
      };

      let cellContent = normalizedValue === 'MATCH' ? 'MATCH' : '\u25B3 MISMATCH';
      if (reconCheck && normalizedValue === 'MATCH') {
        cellContent = formatCount(row?.[reconCheck.rightKey]) || 'MATCH';
      }
      if (reconCheck && normalizedValue === 'MISMATCH') {
        const left = Number(String(row?.[reconCheck.leftKey] ?? '').replace(/,/g, ''));
        const right = Number(String(row?.[reconCheck.rightKey] ?? '').replace(/,/g, ''));
        if (Number.isFinite(left) && Number.isFinite(right) && left !== right) {
          cellContent = `\u25B3 ${Math.abs(left - right).toLocaleString('en-US')}`;
        }
      }

      const tooltipLeft = reconCheck ? formatCount(row?.[reconCheck.leftKey]) : null;
      const tooltipRight = reconCheck ? formatCount(row?.[reconCheck.rightKey]) : null;
      const tooltipValue = tooltipLeft && tooltipRight
        ? `${tooltipLeft}/${tooltipRight}`
        : null;

      return (
        <Tooltip
          title={
            <Box sx={{ textAlign: 'center', lineHeight: 1.15, color: '#000000', fontStyle: 'italic' }}>
              <Box sx={{ fontWeight: 700 }}>
                {normalizedValue === 'MATCH' ? 'Match' : 'Mismatch'}
              </Box>
              {tooltipValue && <Box>{tooltipValue}</Box>}
            </Box>
          }
          placement="top"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: { offset: [0, -15] },
                },
              ],
            },
          }}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: '#EFF6FF !important',
                color: '#000000 !important',
                border: '1px solid #D7E3F4',
                borderRadius: '12px',
                boxShadow: '-4px 4px 10px 0 rgba(0, 0, 0, 0.2)',
              },
            },
            arrow: {
              sx: {
                color: '#EFF6FF !important',
              },
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: normalizedValue === 'MATCH' ? '#01C00833 !important' : '#FE262633 !important',
              color: normalizedValue === 'MATCH' ? '#01C008 !important' : '#FE2626 !important',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'default',
            }}
          >
            {cellContent}
          </Box>
        </Tooltip>
      );
    }

    return formattedValue;
  },
}));

const DataReconciliationDashboard = () => {
  const [searchParams] = useSearchParams();
  const activeProgram = searchParams.get('reconTab') === 'master' ? 'Master' : 'Transactional';
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const tableTitles = {
    Master: 'Consolidated Master Data Recon Status',
    Transactional: 'Consolidated Transactional Data Recon Status',
  } as const;
  const { tableViews, handleExport, dateRangePayload } = useDataReconciliation(activeProgram);
  const activeTable = tableViews.find(({ program }) => program === activeProgram);

  useEffect(() => {
    setPage(1);
  }, [activeProgram, dateRangePayload]);

  return (
    <PageWrapper sx={{ minHeight: 'calc(100vh - 120px)' }}>
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          width: '100%',
          '& .rdg-cell.reconciliation-status-cell': {
            position: 'relative !important',
            padding: '0 !important',
          },
        }}
      >
        {activeTable && (
          <Box key={activeTable.program} sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
            <CustomizedTable
              tableTitle={tableTitles[activeTable.program]}
              tableData={activeTable.rows.slice((page - 1) * pageSize, page * pageSize)}
              total={activeTable.total || activeTable.rows.length}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              onSorting={activeTable.onSorting}
              sortingInfo={activeTable.sortingInfo}
              defaultColumns={activeTable.programColumns.map((column) => column.name)}
              tableType={`data-reconciliation-${activeTable.program.toLowerCase().replace(/\s/g, '-')}`}
              columns={getStyledColumns(activeTable.programColumns)}
              hasGroupBy={false}
              isFetching={activeTable.isLoading}
              showExport={true}
              isSortIcon={true}
              isPrevNextPagination={true}
              fileName={`${activeTable.program.replace(/\s+/g, '_')}_Data_Reconciliation.xlsx`}
              exportTableData={(preferredColumnNames: string[]) => handleExport(activeTable.program, activeTable.programColumns, preferredColumnNames)}
            />
          </Box>
        )}
      </Box>
    </PageWrapper>
  );
};

export default DataReconciliationDashboard;